// ProctorGuard — enforces tab/window integrity during assessments.
// Shows a warning overlay on first violation, ends after max violations.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AlertTriangle, Shield, Eye } from 'lucide-react';
import * as faceapi from 'face-api.js';

// ── Face detector hook ───────────────────────────────────────────────────────
// Polls the webcam feed every FACE_POLL_MS using face-api.js (TinyFaceDetector).
// Works in ALL browsers — no experimental flags needed.
// Fires onNoFace() when candidate is not visible, onMultiFace(count) when extra
// people are detected.
//
// Usage:
//   useFaceDetector({ active: phase === "exam", videoRef, onNoFace, onMultiFace })

const FACE_POLL_MS     = 3000;  // check every 3 seconds
const FACE_COOLDOWN_MS = 12000; // min gap between consecutive face warnings

let faceApiLoaded = false;
let faceApiLoading = false;

async function loadFaceApi() {
  if (faceApiLoaded) return true;
  if (faceApiLoading) {
    // Wait for the other load to finish
    await new Promise(resolve => {
      const check = setInterval(() => { if (faceApiLoaded) { clearInterval(check); resolve(); } }, 100);
    });
    return true;
  }
  faceApiLoading = true;
  try {
    const MODEL_URL = `${process.env.PUBLIC_URL}/models`;
    await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
    faceApiLoaded = true;
    return true;
  } catch (e) {
    console.warn('[FaceDetector] model load failed:', e.message);
    faceApiLoading = false;
    return false;
  }
}

export function useFaceDetector({ active, videoRef, onNoFace, onMultiFace }) {
  const intervalRef = useRef(null);
  const lastWarnRef = useRef(0);
  const loadedRef   = useRef(false);

  useEffect(() => {
    if (!active) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }

    let cancelled = false;

    (async () => {
      const ok = await loadFaceApi();
      if (!ok || cancelled) return;
      loadedRef.current = true;

      intervalRef.current = setInterval(async () => {
        const video = videoRef?.current;
        if (!video || video.readyState < 2 || video.paused) return;

        try {
          const detections = await faceapi.detectAllFaces(
            video,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.4 })
          );
          const count = detections.length;
          const now   = Date.now();
          if (now - lastWarnRef.current < FACE_COOLDOWN_MS) return;

          if (count === 0) {
            lastWarnRef.current = now;
            onNoFace?.();
          } else if (count > 1) {
            lastWarnRef.current = now;
            onMultiFace?.(count);
          }
        } catch { /* frame not ready — skip */ }
      }, FACE_POLL_MS);
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  }, [active, videoRef, onNoFace, onMultiFace]);
}

// ── Noise detector hook ───────────────────────────────────────────────────────
// Monitors the microphone for sustained background noise during interviews.
// If the RMS volume exceeds NOISE_THRESHOLD for NOISE_SUSTAIN_MS continuously,
// it fires onNoise(). Separate from ElevenLabs mic — purely for monitoring.
//
// Usage:
//   useNoiseDetector({ active: isInterviewActive, onNoise: () => triggerViolation(...) })

const NOISE_THRESHOLD   = 0.08;  // RMS 0-1; ~moderate background noise
const NOISE_SUSTAIN_MS  = 3000;  // must stay loud for 3s before warning fires
const NOISE_COOLDOWN_MS = 10000; // min gap between consecutive noise warnings
// High zero-crossing rate on top of loud signal → likely speech (TV, another person)
const SPEECH_ZCR_THRESHOLD = 0.15; // zero-crossings per sample — voice-like signals

// `stream`: a stream the page already holds (the written test asks for camera
// and microphone together, once, before it starts). Given one, the detector
// listens to it and never opens — or closes — the microphone itself, so no
// permission prompt appears mid-exam.
export function useNoiseDetector({ active, onNoise, stream: given = null }) {
  const audioCtxRef    = useRef(null);
  const analyserRef    = useRef(null);
  const streamRef      = useRef(null);
  const rafRef         = useRef(null);
  const noiseStartRef  = useRef(null);  // timestamp when loud noise began
  const lastWarnRef    = useRef(0);     // timestamp of last noise warning

  const stop = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (!given) streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    analyserRef.current = null;
    noiseStartRef.current = null;
  }, [given]);

  useEffect(() => {
    if (!active) { stop(); return; }

    let cancelled = false;

    (async () => {
      try {
        const stream = given && given.getAudioTracks().length
          ? new MediaStream(given.getAudioTracks())
          : await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        if (cancelled) { if (!given) stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const ctx      = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        ctx.createMediaStreamSource(stream).connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;

        const buf = new Float32Array(analyser.fftSize);

        const tick = () => {
          if (!analyserRef.current) return;
          analyser.getFloatTimeDomainData(buf);
          // Compute RMS
          const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length);
          // Zero-crossing rate — voice signals have higher ZCR than pure noise
          let zcr = 0;
          for (let i = 1; i < buf.length; i++) {
            if ((buf[i-1] >= 0) !== (buf[i] >= 0)) zcr++;
          }
          zcr /= buf.length;
          const now = Date.now();
          const isSpeechLike = rms > NOISE_THRESHOLD && zcr > SPEECH_ZCR_THRESHOLD;

          if (rms > NOISE_THRESHOLD) {
            if (!noiseStartRef.current) noiseStartRef.current = now;
            else if (now - noiseStartRef.current >= NOISE_SUSTAIN_MS) {
              if (now - lastWarnRef.current >= NOISE_COOLDOWN_MS) {
                lastWarnRef.current = now;
                noiseStartRef.current = null;
                onNoise?.(isSpeechLike ? 'background_speech' : 'background_noise');
              }
            }
          } else {
            noiseStartRef.current = null;
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        // Mic permission denied or not available — silently skip noise detection
      }
    })();

    return () => { cancelled = true; stop(); };
  }, [active, onNoise, stop, given]);
}

const MAX_WARNINGS   = 3;   // auto-end after this many violations
const GRACE_MS       = 2000; // grace period before counting a violation

export function useProctorGuard({ active, onViolation, onEnd }) {
  const [warnings, setWarnings]         = useState(0);
  const [showWarning, setShowWarning]   = useState(false);
  const [warningMsg, setWarningMsg]     = useState('');
  const timerRef = useRef(null);

  const triggerViolation = useCallback((reason) => {
    setWarnings(prev => {
      const next = prev + 1;
      setWarningMsg(reason);
      setShowWarning(true);
      if (onViolation) onViolation(reason, next);
      if (next >= MAX_WARNINGS && onEnd) {
        setTimeout(() => onEnd('proctoring_violation'), 2000);
      }
      return next;
    });
  }, [onViolation, onEnd]);

  const dismissWarning = useCallback(() => setShowWarning(false), []);

  useEffect(() => {
    if (!active) return;

    // ── Visibility change (tab switch / minimise) ──
    const onVisChange = () => {
      if (document.hidden) {
        timerRef.current = setTimeout(() => {
          triggerViolation('You switched tabs or minimised the window.');
        }, GRACE_MS);
      } else {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      }
    };

    // ── Window blur (alt-tab, clicked another app) ──
    const onBlur = () => {
      timerRef.current = setTimeout(() => {
        triggerViolation('You left the assessment window.');
      }, GRACE_MS);
    };
    const onFocus = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };

    // ── Block keyboard shortcuts that open new tabs/windows ──
    const onKeyDown = (e) => {
      // Ctrl/Cmd + T (new tab), Ctrl/Cmd + N (new window), Ctrl/Cmd + W (close tab)
      if ((e.ctrlKey || e.metaKey) && ['t','n','w'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        triggerViolation(`Keyboard shortcut blocked (${e.ctrlKey ? 'Ctrl' : 'Cmd'}+${e.key.toUpperCase()} is not allowed).`);
      }
    };

    // ── Block right-click context menu ──
    const onContextMenu = (e) => {
      e.preventDefault();
      triggerViolation('Right-click is disabled during assessments.');
    };

    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('blur',        onBlur);
    window.addEventListener('focus',       onFocus);
    document.addEventListener('keydown',   onKeyDown);
    document.addEventListener('contextmenu', onContextMenu);

    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('blur',        onBlur);
      window.removeEventListener('focus',       onFocus);
      document.removeEventListener('keydown',   onKeyDown);
      document.removeEventListener('contextmenu', onContextMenu);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, triggerViolation]);

  return { warnings, showWarning, warningMsg, dismissWarning, triggerViolation };
}

// ── Warning overlay component ─────────────────────────────────────────────
export function ProctorWarningOverlay({ show, message, warnings, maxWarnings, onDismiss }) {
  if (!show) return null;

  const isLastWarning = warnings >= maxWarnings - 1;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center border-4 ${isLastWarning ? 'border-red-500' : 'border-orange-400'}`}>
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isLastWarning ? 'bg-red-100' : 'bg-orange-100'}`}>
          <AlertTriangle className={`w-8 h-8 ${isLastWarning ? 'text-red-600' : 'text-orange-500'}`} />
        </div>

        <h2 className={`text-xl font-extrabold mb-2 ${isLastWarning ? 'text-red-700' : 'text-orange-700'}`}>
          {isLastWarning ? 'Final Warning' : `Proctoring Violation — Warning ${warnings} of ${maxWarnings}`}
        </h2>

        <p className="text-gray-700 text-sm mb-3 leading-relaxed">{message}</p>

        {isLastWarning ? (
          <p className="text-red-600 font-semibold text-sm mb-5">
            This is your final warning. Any further violation will immediately end your assessment and be reported.
          </p>
        ) : (
          <p className="text-gray-500 text-sm mb-5">
            This assessment is proctored. Do not switch tabs, open new windows, or use keyboard shortcuts.
            <strong className="text-orange-600"> {maxWarnings - warnings} warning{maxWarnings - warnings !== 1 ? 's' : ''} remaining.</strong>
          </p>
        )}

        <div className="flex items-center justify-center gap-2 mb-4 text-xs text-gray-400">
          <Shield className="w-3.5 h-3.5" />
          <span>Violation recorded and will be included in your assessment report</span>
        </div>

        <button
          onClick={onDismiss}
          className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${isLastWarning ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-500 hover:bg-orange-600'}`}
        >
          I Understand — Return to Assessment
        </button>
      </div>
    </div>
  );
}

// ── Proctor status badge (shown in top corner during active assessment) ──
export function ProctorBadge({ warnings, maxWarnings }) {
  const colour = warnings === 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : warnings < maxWarnings - 1 ? 'bg-orange-50 text-orange-700 border-orange-200'
    : 'bg-red-50 text-red-700 border-red-200';

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${colour}`}>
      <Eye className="w-3.5 h-3.5" />
      Proctored {warnings > 0 && `· ${warnings} warning${warnings !== 1 ? 's' : ''}`}
    </div>
  );
}
